import * as React from 'react';

import {Avatar} from 'shells/avatar/avatar';
import {colors} from 'shells/colors/colors';
import {NavIconSettingsIcon} from 'shells/icon/navigation/nav-icon-settings';
import {GoalAudienceIcon} from 'shells/icon/svg/goal-audience-icon';
import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon/select-two-line-icon';
import {SelectionModal} from 'shells/selection-modal/selection-modal';

import type {AssignedUserFragment, TeamFragment} from 'nutshell-graphql-types';

import {useGetTeams} from '../../core/graphql/hooks/use-get-teams';

import {useGetUsersForManagedMeetings} from './graphql/hooks/get-users-for-managed-meetings';

export type SelectionType = 'users' | 'teams' | 'users-teams';

type Props = {
    isOpen: boolean;
    type: SelectionType;
    selected: AssignedUserFragment | TeamFragment[];
    onSelection: (arg1: {selectedUsers: string[]; selectedTeams: string[]}) => void;
    onClose: () => void;
};

type OptionType = 'user' | 'team';

export type Option = TwoLineOptionWithIcon<OptionType>;

export function UsersTeamsSelectionModal(props: Props) {
    const shouldSkipUsers = props.type === 'teams';
    const {users} = useGetUsersForManagedMeetings(shouldSkipUsers);

    const shouldSkipTeams = props.type === 'users';
    const {teams} = useGetTeams(shouldSkipTeams);

    const usersAndTeams = [...users, ...teams];
    // @ts-expect-error
    const allOptions = convertToOptionsObj(usersAndTeams);

    const selectedOptions = convertToOptionsObj(props.selected);

    return (
        <SelectionModal
            title='Assign this meeting'
            subtitle={getSubtitle(props.type)}
            options={allOptions}
            selectedOptions={selectedOptions}
            selectPlaceholder={getSelectPlaceholder(props.type)}
            selectFooterButton={{
                label: getSelectFooterButtonLabel(props.type),
                icon: NavIconSettingsIcon,
                onClick: () => {
                    window.open('/setup/user', '_blank');
                },
            }}
            isOpen={props.isOpen}
            onClose={props.onClose}
            buttonConfig={{
                type: 'normal',
                onSelection: (selected) => {
                    const selectedUsers = selected
                        .filter((option) => option.metaData === 'user')
                        .map((option) => option.value);
                    const selectedTeams = selected
                        .filter((option) => option.metaData === 'team')
                        .map((option) => option.value);
                    props.onSelection({selectedUsers, selectedTeams});
                },
            }}
            nothingFoundState={getNothingFoundState(props.type)}
        />
    );
}

function convertToOptionsObj(models: AssignedUserFragment | TeamFragment[]): Option[] {
    // @ts-expect-error
    return models.map((model) => ({
        value: model.id,
        label: model.name,
        sublabel:
            model.__typename === 'User'
                ? model.emailAddresses[0].address
                : `${model.numUsers} members`,
        iconNode:
            model.__typename === 'Team' ? (
                // @ts-expect-error
                <GoalAudienceIcon size={23} color={colors.orangeDk} />
            ) : (
                <Avatar
                    size={23}
                    avatarUrl={model.avatarUrl}
                    initials={model.initials}
                    circular={true}
                    type='users'
                />
            ),
        metaData: model.__typename === 'User' ? 'user' : 'team',
    }));
}

function getSubtitle(type: 'users' | 'teams' | 'users-teams'): string {
    switch (type) {
        case 'users':
            return 'Users assigned';
        case 'teams':
            return 'Teams assigned';
        case 'users-teams':
        default:
            return 'Users and teams assigned';
    }
}

function getSelectPlaceholder(type: 'users' | 'teams' | 'users-teams'): string {
    switch (type) {
        case 'users':
            return 'Search for a user';
        case 'teams':
            return 'Search for a team';
        case 'users-teams':
        default:
            return 'Search for a user or a team';
    }
}

function getSelectFooterButtonLabel(type: 'users' | 'teams' | 'users-teams'): string {
    switch (type) {
        case 'users':
            return 'Manage users';
        case 'teams':
            return 'Manage teams';
        case 'users-teams':
        default:
            return 'Manage users and teams';
    }
}

function getNothingFoundState(type: 'users' | 'teams' | 'users-teams'): string {
    switch (type) {
        case 'users':
            return 'Selected users will be displayed here';
        case 'teams':
            return 'Selected teams will be displayed here';
        case 'users-teams':
        default:
            return 'Selected users and teams will be displayed here';
    }
}
